import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { IndianRupee, CreditCard, FileText, MessageSquareWarning, Loader2, ShieldCheck } from "lucide-react";
import { IMAGES, ALL_CLASSES, SCHOOL } from "@/lib/constants";
import { PageBanner } from "@/components/PageBanner";
import { SectionHeading } from "@/components/SectionHeading";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/utils2";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const GUIDELINES = [
  "Ensure your child attends school regularly and on time (8:00 AM).",
  "Check the school diary and Notice Board daily for updates.",
  "Complete fee payments within the first week of every month.",
  "Attend parent-teacher meetings for your child's progress.",
  "Inform the school in advance about any leave of absence.",
  "Ensure your child is in complete, clean uniform every day.",
];

export default function ParentCorner() {
  const [packages, setPackages] = useState([]);
  const [pay, setPay] = useState({ package_id: "", student_name: "", student_class: "" });
  const [payLoading, setPayLoading] = useState(false);
  const [complaint, setComplaint] = useState({ name: "", phone: "", email: "", type: "Suggestion", subject: "", message: "" });
  const [cLoading, setCLoading] = useState(false);

  useEffect(() => { api.getFeePackages().then((r) => setPackages(r.data)).catch(() => {}); }, []);

  const startPayment = async () => {
    if (!pay.package_id) { toast.error("Please select a fee package."); return; }
    setPayLoading(true);
    try {
      const res = await api.checkout({ ...pay, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Payment could not be started.");
      setPayLoading(false);
    }
  };

  const submitComplaint = async (e) => {
    e.preventDefault();
    if (!complaint.name || !complaint.phone || !complaint.subject || !complaint.message) {
      toast.error("Please fill all required fields."); return;
    }
    setCLoading(true);
    try {
      await api.createComplaint(complaint);
      toast.success("Thank you! Your message has been received.");
      setComplaint({ name: "", phone: "", email: "", type: "Suggestion", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit.");
    } finally { setCLoading(false); }
  };

  const selected = packages.find((p) => p.id === pay.package_id);

  return (
    <div>
      <PageBanner title="Parent Corner" subtitle="Pay fees online, read guidelines and share your feedback with us." image={IMAGES.hero} />

      {/* Fee payment */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-8 items-start">
        <div className="bg-navy rounded-3xl p-8 text-white shadow-xl">
          <div className="flex items-center gap-2 text-gold mb-2"><CreditCard className="h-6 w-6" /><span className="text-sm font-bold uppercase tracking-widest">Online Fee Payment</span></div>
          <h3 className="font-head font-extrabold text-2xl mb-6">Pay your child's fees securely</h3>
          <div className="space-y-4">
            <Select value={pay.package_id} onValueChange={(v) => setPay((p) => ({ ...p, package_id: v }))}>
              <SelectTrigger data-testid="fee-package-select" className="bg-white/10 border-white/20 text-white"><SelectValue placeholder="Select Fee Package *" /></SelectTrigger>
              <SelectContent>
                {packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.label} — ₹{p.amount.toLocaleString("en-IN")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="fee-student-name" placeholder="Student Name" className="bg-white/10 border-white/20 text-white placeholder:text-slate-400" value={pay.student_name} onChange={(e) => setPay((p) => ({ ...p, student_name: e.target.value }))} />
            <Select value={pay.student_class} onValueChange={(v) => setPay((p) => ({ ...p, student_class: v }))}>
              <SelectTrigger data-testid="fee-student-class" className="bg-white/10 border-white/20 text-white"><SelectValue placeholder="Class" /></SelectTrigger>
              <SelectContent>{ALL_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            {selected && (
              <div className="flex items-center justify-between bg-white/10 rounded-xl px-4 py-3">
                <span className="text-sm text-slate-200">Amount payable</span>
                <span className="font-head font-black text-2xl text-gold flex items-center"><IndianRupee className="h-5 w-5" />{selected.amount.toLocaleString("en-IN")}</span>
              </div>
            )}
            <Button onClick={startPayment} disabled={payLoading} data-testid="fee-pay-btn" className="w-full bg-brandred hover:bg-brandred/90 text-white rounded-full h-12 font-semibold">
              {payLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Proceed to Pay</>}
            </Button>
            <p className="text-xs text-slate-400 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Secure payment powered by Stripe (test mode).</p>
          </div>
        </div>

        {/* Guidelines */}
        <div>
          <SectionHeading label="For Parents" title="Parent Guidelines" />
          <ul className="space-y-3">
            {GUIDELINES.map((g, i) => (
              <li key={i} className="flex gap-3 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm text-sm text-slate-700">
                <span className="h-6 w-6 rounded-full bg-brandteal/15 text-brandteal grid place-items-center font-bold shrink-0 text-xs">{i + 1}</span>{g}
              </li>
            ))}
          </ul>
          <div className="mt-4 bg-gold/15 border border-gold/40 rounded-2xl p-4 text-sm text-navy flex items-center gap-2">
            <FileText className="h-5 w-5 text-gold" /> Circulars & notices are available in the <a href="/notices" className="font-bold underline">Notices</a> section.
          </div>
        </div>
      </section>

      {/* Complaint / Suggestion */}
      <section className="bg-[#F3F4F6] py-20">
        <div className="max-w-3xl mx-auto px-6">
          <SectionHeading center label="We're Listening" title="Complaint / Suggestion Box" desc="Your feedback helps us serve your child better." />
          <form onSubmit={submitComplaint} data-testid="complaint-form" className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm grid sm:grid-cols-2 gap-4">
            <Input data-testid="cp-name" placeholder="Your Name *" value={complaint.name} onChange={(e) => setComplaint((c) => ({ ...c, name: e.target.value }))} />
            <Input data-testid="cp-phone" placeholder="Phone *" value={complaint.phone} onChange={(e) => setComplaint((c) => ({ ...c, phone: e.target.value }))} />
            <Input data-testid="cp-email" type="email" placeholder="Email" value={complaint.email} onChange={(e) => setComplaint((c) => ({ ...c, email: e.target.value }))} />
            <Select value={complaint.type} onValueChange={(v) => setComplaint((c) => ({ ...c, type: v }))}>
              <SelectTrigger data-testid="cp-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Suggestion">Suggestion</SelectItem><SelectItem value="Complaint">Complaint</SelectItem><SelectItem value="Feedback">Feedback</SelectItem></SelectContent>
            </Select>
            <Input data-testid="cp-subject" className="sm:col-span-2" placeholder="Subject *" value={complaint.subject} onChange={(e) => setComplaint((c) => ({ ...c, subject: e.target.value }))} />
            <Textarea data-testid="cp-message" className="sm:col-span-2" rows={4} placeholder="Your message *" value={complaint.message} onChange={(e) => setComplaint((c) => ({ ...c, message: e.target.value }))} />
            <Button type="submit" disabled={cLoading} data-testid="cp-submit" className="sm:col-span-2 bg-navy hover:bg-navy/90 text-white rounded-full h-12 font-semibold">
              {cLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MessageSquareWarning className="mr-1.5 h-4 w-4" /> Submit</>}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
